/* eslint-disable import/no-anonymous-default-export */
import type {
  AdminOrderSortBy,
  AdminSortOrder,
  AdminUserSortBy,
} from '@v2/views/admin/sorting';

type AdminOrdersPaginatedParams = {
  offset?: number;
  pageSize?: number;
  currency?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  ticketOrderStatus?: string | null;
  hotelOrderStatus?: string | null;
  status?: string | null;
  tripFilter?: string | null;
  query?: string | null;
  sortBy?: AdminOrderSortBy | null;
  sortOrder?: AdminSortOrder | null;
};

type AdminUserListPaginatedParams = {
  offset?: number;
  userRole?: string;
  userRoles?: string[];
  status?: string;
  startDate?: string;
  endDate?: string;
  query?: string;
  pageSize?: number;
  agencies?: string;
  sortBy?: AdminUserSortBy | null;
  sortOrder?: AdminSortOrder | null;
};

export default {
  signup: '/auth/signup',
  affiliateSignup: '/auth/affiliate-signup',

  login: '/auth/login',
  partialSignup: '/auth/partial-signup',
  agencyLogin: '/auth/agency-login',
  sendPasswordlessCode: '/auth/passwordless-send-verification-code',
  validatePasswordlessCode: '/auth/passwordless-validate-code',
  sendVerificationCode: '/auth/send-verification-code',
  passwordResetWithCode: '/auth/password-reset-with-code',
  userExist: '/auth/user-exists',
  socialLogin: '/auth/social-login',
  refresh: '/auth/refresh-token',
  agencySignup: '/auth/agency-signup',
  assignAgenctoUser: '/agency-team-member/assign-agency-to-user',
  agentSignup: '/auth/agent-signup',
  resetPassword: '/auth/reset-password',
  changePassword: '/auth/change-password',
  updatePassword: '/auth/update-password',
  confirmEmail: '/auth/confirm-email',
  user: '/auth/user',
  editUserProfileInfo: '/auth/edit-profile-info',
  listAirports: '/flight/airports',
  airportsByTeamName: '/flight/airports/by-team-name',
  airportsByCity: '/flight/airports/by-city',
  search: '/search-proxy',
  airline: '/airline',
  searchFlights: '/flight/search-flight',
  favorite: '/dashboard/favorite',
  getId: '/get-id',
  eventsProxy: '/te/proxy',
  getBrandedFares: '/flight/branded-fares',
  preBookFlight: '/flight/pre-book-flight',
  bookFlight: '/flight/book-flight',

  teOrder: '/te/order',
  teTax: '/te/tax',
  teTicketAvailability: '/te/ticket-availability',
  getHeaderTeams: '/teams',
  getFollowedEvents: '/get-followed-events',
  geocodeAws: '/aws-places/geocode',
  getLatLngAws: '/aws-places/get-lat-lng',
  getUtcOffset: '/google-api/get-utc-offset',
  travelPro: '/booking',
  travelProPost: '/booking/post',
  propertySearch: '/property/search',
  propertyDetails: '/property/details',
  propertyPriceCheck: '/property/price-check',
  holdPropertyBooking: '/property/hold-property-booking',
  resumePropertyBooking: '/property/resume-property-booking',
  cancelPropertyBooking: '/property/cancel-property-booking',
  createOrder: '/payment',
  pay: '/payment/pay',
  completePayment: '/transaction/moneris',
  verifyCard: '/transaction/card-verification',
  userOrders: '/order/user-orders',
  resendOrderEmail: '/order/resend-order-email',
  adminOrders: '/order/admin-orders',
  adminOrdersPaginated: (params: AdminOrdersPaginatedParams) => {
    const searchParams = new URLSearchParams();
    if (params.pageSize)
      searchParams.append('pageSize', params.pageSize.toString());
    if (params.currency)
      searchParams.append('currency', params.currency.toString());
    if (params.startDate) searchParams.append('startDate', params.startDate);
    if (params.endDate) searchParams.append('endDate', params.endDate);
    if (params.ticketOrderStatus)
      searchParams.append('ticketOrderStatus', params.ticketOrderStatus);
    if (params.hotelOrderStatus)
      searchParams.append('hotelOrderStatus', params.hotelOrderStatus);
    if (params.status) searchParams.append('status', params.status);
    if (params.tripFilter && params.tripFilter !== 'all')
      searchParams.append('tripFilter', params.tripFilter);
    if (params.query) searchParams.append('query', params.query);
    if (params.sortBy) searchParams.append('sortBy', params.sortBy);
    if (params.sortOrder) searchParams.append('sortOrder', params.sortOrder);

    return `/order/admin-orders-paginated/${params.offset}?${searchParams.toString()}`;
  },
  adminRemittances: (params: {
    offset?: number;
    pageSize?: number;
    commissionStatus?: string | null;
    startDate?: string | null;
    endDate?: string | null;
    query?: string | null;
    sortBy?: string;
    sortOrder?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params.pageSize)
      searchParams.append('pageSize', params.pageSize.toString());
    if (params.commissionStatus)
      searchParams.append('commissionStatus', params.commissionStatus);
    if (params.startDate) searchParams.append('startDate', params.startDate);
    if (params.endDate) searchParams.append('endDate', params.endDate);
    if (params.query) searchParams.append('query', params.query);
    if (params.sortBy) searchParams.append('sortBy', params.sortBy);
    if (params.sortOrder) searchParams.append('sortOrder', params.sortOrder);

    return `/affiliate-referrals/list-paginated/${params.offset || 0}?${searchParams.toString()}`;
  },
  adminUserListPaginated: (params: AdminUserListPaginatedParams) => {
    const queryParams = new URLSearchParams({
      pageSize: (params.pageSize || 10).toString(),
      ...(params.userRole && { userRole: params.userRole }),
      ...(params.userRoles?.length && {
        userRoles: params.userRoles.join(','),
      }),
      ...(params.status && { status: params.status }),
      ...(params.startDate && { startDate: params.startDate }),
      ...(params.endDate && { endDate: params.endDate }),
      ...(params.query && { query: params.query }),
      ...(params.agencies && { agencies: params.agencies }),
      ...(params.sortBy && { sortBy: params.sortBy }),
      ...(params.sortOrder && { sortOrder: params.sortOrder }),
    }).toString();

    return `/user/list-paginated/${params.offset || 0}?${queryParams}`;
  },
  userDetails: (id: string) => `/user/${id}`,
  updateUserEmail: (id: string) => `/user/admin/${id}/email`,
  adminChangeUserPassword: (id: string) => `/user/admin/${id}/password`,
  updateOrderStatus: '/order/update-status',
  addOrderComment: '/order-comment',
  addLocation: '/user/add-location',
  userIpAddress: '/user-ip-address',
  updateHotelOrderStatus: '/order/update-hotel-status',
  updateFlightOrderStatus: '/order/update-flight-status',
  updateOrderTicketStatus: '/order/update-ticket-status',
  updateHotelStatus: '/hotel/update-status',
  updateFlightStatus: '/flight/update-booking-status',
  createEstOrder: '/order',
  orderSteps: (orderId: number | string) => `/order/${orderId}/steps`,
  retryOrderStep: '/order/retry-step',
  replaceOrderCard: (orderId: number | string) =>
    `/order/${orderId}/replace-card`,
  prepareReplaceOrderItem: (orderId: number | string) =>
    `/order/${orderId}/replace/prepare`,
  confirmReplaceOrderItem: (orderId: number | string) =>
    `/order/${orderId}/replace/confirm`,
  updateOrderTicketId: (orderId: number | string) =>
    `/order/${orderId}/ticket-id`,
  setOrderItineraryId: (orderId: number | string) =>
    `/order/${orderId}/itinerary-id`,
  resolveRefundOwed: (orderId: number | string) =>
    `/order/${orderId}/refund-owed/resolve`,
  banner: '/banner',
  banners: '/banner/banners',
  localRefresh: '/auth/refresh',
  emergencyNumber: '/emergency-number',
  orderDetails: '/order/details',
  orderLogs: '/order-log',
  hotelOrderLogs: '/order-log/hotel',
  ticketOrderLogs: '/order-log/ticket',
  cancelOrderItinerary: '/order/cancel-order-itinerary',
  adminRefundOrder: '/order/admin-refund',
  itinerary: '/property/itinerary',
  usersList: '/user/list',
  updateUserRole: '/user/change-role',
  addUser: '/user/add-user',
  resendPassword: '/user/resend-password',
  deleteAccount: '/user/delete-account',
  uploadProfileImage: '/user/upload-profile-image',
  deleteUser: '/user/admin-delete',
  cacheLogs: '/property/cache-logs',
  cacheUpdate: '/property/update-cache',
  cacheRetryUnzip: '/property/update-cache/retry-unzip',
  cacheRetryUpdate: '/property/update-cache/retry-update',
  ordersReport: '/report/orders',
  usersReport: '/report/users',
  usersKpiReport: '/report/users-kpi',
  postVisit: '/report/visit',
  xmlSitemap: '/xml/sitemap',
  reportError: '/sendgrid/report-error',
  logOperation: '/operation-log',
  shareOffer: '/sendgrid/share-offer',
  shareQuotation: '/sendgrid/share-quotation',
  resendConfirmation: '/user/resend-confirmation',
  placeGeocode: '/places/geocode',
  placesLatLng: '/places/lat-lng',
  adminDiscountCodes: '/discount-code/admin-discount-codes',
  discountCode: '/discount-code',
  checkDiscountCode: '/discount-code/validate',
  addDiscountCode: '/discount-code/create',
  addDiscountCodeComment: '/discount-code/comment',
  affiliate: '/affiliate',
  adminPatchAffiliate: '/affiliate/admin',
  getAffiliateData: '/affiliate/me',
  affiliateSiteVisit: '/affiliate-site-visit',
  affiliateReferral: '/affiliate-referral',
  getAffiliateSalesData: '/affiliate-referral/me',
  adminAssignAffiliateToOrder: (orderId: number | string) =>
    `/affiliate-referral/admin/assign/${orderId}`,
  adminAgentCommissionRates: (userId: number | string) =>
    `/affiliate-referral/admin/commission-rates/${userId}`,
  getAffiliateSiteVisits: '/affiliate-site-visit/me',
  getPartnerSales: '/partner/sales',
  getPartnerPerformances: '/partner/performances',
  affiliateDiscountCodes: '/discount-code/affiliate',
  contactUs: '/sendgrid/contact-us',
  concertsLanding: '/concerts',

  // events
  event: '/event',
  eventLocation: '/event/location',
  eventsCity: '/event/city',
  eventsCategory: '/event/category',
  eventPopularPerformers: '/event/popular-performers',
  eventPopularPerformerEvents: '/event/popular-performers-events',
  eventTrendings: '/event/trending',
  affiliateAdminId: (id: any) => `/affiliate/admin/${id}`,

  // newsletter
  addTagToUser: '/newsletter/add-tag-to-user',
  removeTagFromUser: '/newsletter/remove-tag-from-user',

  // Travelers
  traveler: '/traveler',
  travelerId: (travelerId: number) =>
    `/traveler/${travelerId}?travelerId=${travelerId}`,
  getUserTravelers: '/traveler/user-travelers',

  // AGENCY
  agencyMe: '/agency/me',
  agency: '/agency',
  agencyPerformance: (agencyId: number) => `/agency/${agencyId}/performance`,
  agencyDetailedPerformance: (agencyId: number) =>
    `/agency/${agencyId}/detailed-performance`,
  agencyReports: (agencyId: number) => `/report/agency/${agencyId}/orders`,
  agencyDetails: (agencyId: number) => `/agency/${agencyId}/details`,
  agencySales: (agencyId: number) => `/agency/${agencyId}/sales`,
  agencyAffiliatesCodes: (agencyId: number) =>
    `/agency/${agencyId}/affiliate-codes`,
  agencyDiscountCode: (agencyId: number, codeId) =>
    `/agency/${agencyId}/discount-code/${codeId}`,
  agencyTeamMember: (agencyId: any) => `/agency-team-member/${agencyId}`,
  agencyTeamMemberInvite: (agencyId: number) =>
    `/agency-team-member-invites/${agencyId}`,
  agencyTeamMemberInviteResend: (agencyTeamMemberId: number) =>
    `/agency-team-member-invites/resend/${agencyTeamMemberId}`,
  agentDetailsUnderAgency: ({ agencyId, agentUserId }) =>
    `/agency/${agencyId}/agent/${agentUserId}/details`,
  agencyAdmin: '/agency/admin',
  agencyAdminList: (params: {
    status?: string;
    sortBy?: string;
    sortOrder?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params.status) searchParams.append('status', params.status);
    if (params.sortBy) searchParams.append('sortBy', params.sortBy);
    if (params.sortOrder) searchParams.append('sortOrder', params.sortOrder);
    const queryString = searchParams.toString();
    return queryString ? `/agency/admin?${queryString}` : '/agency/admin';
  },
  agencyLatestUserInvites:
    '/agency-team-member-invites-link/latest-user-invites',
  createAgencyTeamMemberInviteLink: (agencyId: number) =>
    `/agency-team-member-invites-link/${agencyId}/invite-link`,
  getAgencyTeamMemberInviteLinks: (agencyId: number, token: string) =>
    `/agency-team-member-invites-link/${agencyId}/invite-links?token=${token}`,
  deleteAgencyTeamMemberInviteLink: (inviteId: number) =>
    `/agency-team-member-invites-link/invite/${inviteId}`,
  toggleAgencyTeamMemberInviteLink: () =>
    `/agency-team-member-invites-link/toggle-status`,
  validateInviteLink: (inviteToken: string | undefined) =>
    `/agency-team-member-invites-link/validate/${inviteToken}`,

  // ANALYTICS
  track: '/analytics/track',
  identifyUser: '/analytics/identify-user',

  // ANONYMOUS USER
  createAnonymousUser: '/anonymous-user',
  updateAnonymousUser: (anonymousUserId: string) =>
    `/anonymous-user/${anonymousUserId}`,

  // Shopping cart
  shoppingCart: '/shopping-cart',
  shoppingCartId: (cartId: any) => `/shopping-cart/${cartId}`,
  shoppingCartByUuid: (uuid: any) => `/shopping-cart/uuid/${uuid}`,

  whiteLabelConfig: `/white-label-config`,

  // Custom Package Request
  customPackageRequest: '/custom-package-request',
  agentQuote: '/custom-package-request/agent-quote',
  customPackageRequestUnassigned: '/custom-package-request/unassigned',
  customPackageRequestAssignClient: (id: number | string) =>
    `/custom-package-request/${id}/client`,
  customPackageRequestLinkOrder: (id: number | string) =>
    `/custom-package-request/${id}/order`,

  // Client CRM
  client: '/client',
  // Admin CRM view controls, shared by clientList and clientStats:
  //  view=all  -> every client (admins only); default is the caller's own book
  //  agentId / agencyId -> narrow an admin's "all" view to one agent or agency
  clientStats: (
    params: {
      view?: 'mine' | 'all';
      agentId?: number | null;
      agencyId?: number | null;
    } = {}
  ) => {
    const searchParams = new URLSearchParams();
    if (params.view) searchParams.append('view', params.view);
    if (params.agentId != null)
      searchParams.append('agentId', String(params.agentId));
    if (params.agencyId != null)
      searchParams.append('agencyId', String(params.agencyId));
    const queryString = searchParams.toString();
    return queryString ? `/client/stats?${queryString}` : '/client/stats';
  },
  clientById: (id: number | string) => `/client/${id}`,
  clientList: (params: {
    offset?: number;
    query?: string | null;
    pageSize?: number;
    status?: string | null;
    tags?: string[] | null;
    followUpDueToday?: boolean;
    followUpOverdue?: boolean;
    hasActiveQuotes?: boolean;
    hasUpcomingEvents?: boolean;
    view?: 'mine' | 'all';
    agentId?: number | null;
    agencyId?: number | null;
  }) => {
    const offset = params.offset ?? 0;
    const searchParams = new URLSearchParams();
    if (params.query) searchParams.append('query', params.query);
    if (params.status) searchParams.append('status', params.status);
    if (params.tags?.length) searchParams.append('tags', params.tags.join(','));
    if (params.followUpDueToday) {
      searchParams.append('followUpDueToday', 'true');
    }
    if (params.followUpOverdue) {
      searchParams.append('followUpOverdue', 'true');
    }
    if (params.hasActiveQuotes) {
      searchParams.append('hasActiveQuotes', 'true');
    }
    if (params.hasUpcomingEvents) {
      searchParams.append('hasUpcomingEvents', 'true');
    }
    if (params.pageSize != null) {
      searchParams.append('pageSize', String(params.pageSize));
    }
    if (params.view) searchParams.append('view', params.view);
    if (params.agentId != null)
      searchParams.append('agentId', String(params.agentId));
    if (params.agencyId != null)
      searchParams.append('agencyId', String(params.agencyId));
    const queryString = searchParams.toString();
    return queryString
      ? `/client/list/${offset}?${queryString}`
      : `/client/list/${offset}`;
  },
  clientOrders: (id: number | string) => `/client/${id}/orders`,
  clientOrderSearch: (params: {
    offset?: number;
    query?: string | null;
    pageSize?: number;
  }) => {
    const offset = params.offset ?? 0;
    const searchParams = new URLSearchParams();
    if (params.query) searchParams.append('query', params.query);
    if (params.pageSize != null) {
      searchParams.append('pageSize', String(params.pageSize));
    }
    const queryString = searchParams.toString();
    return queryString
      ? `/client/orders/search/${offset}?${queryString}`
      : `/client/orders/search/${offset}`;
  },
  clientQuotes: (id: number | string) => `/client/${id}/quotes`,
  clientQuoteFollowUp: (clientId: number | string, quoteId: number | string) =>
    `/client/${clientId}/quotes/${quoteId}/follow-up`,
  clientQuoteComments: (clientId: number | string, quoteId: number | string) =>
    `/client/${clientId}/quotes/${quoteId}/comments`,
  clientAssignOrder: (id: number | string) => `/client/${id}/assign-order`,
  clientNotes: (id: number | string) => `/client/${id}/notes`,
  clientNote: (id: number | string, noteId: number | string) =>
    `/client/${id}/notes/${noteId}`,
};

export const serverApiPrefix = (path: string) => `/api/v1${path}`;
export const frontApiPrefix = (path: string) => `/api${path}`;
